import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Grid, Group, Image, Paper, Stack, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { NpsScoreCard } from "@testuj.to/react";
import dayjs from "dayjs";
import { downloadImage } from "src/components/LightBox/downloadUtils";
import { CampaignProductResults, CampaignState, File } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { foldRatingBreakup, getNpsBuckets, getStarColor } from "../../overview/results";
import { BarList } from "../components/BarList";
import { ReportSection } from "../components/ReportSection";
import { downloadStampBadge, stampBadgeSrc } from "../components/stampBadge";

export interface ReportEvaluationProps {
    campaign: CampaignQueryDataType;
    productId?: string | null;
    productResults?: CampaignProductResults;
}

// Each evaluation card fills its grid cell so cards in the same row share a height.
const cardStyle = { height: "100%" } as const;

export const ReportEvaluation = ({ campaign, productId, productResults }: ReportEvaluationProps) => {
    const { t } = useTranslation(["common"]);

    // 5★-first distribution rendered as our own bars (star label + count), each
    // row colour-graded by its star level (5 green → 1 red).
    const ratingItems = useMemo(
        () =>
            foldRatingBreakup(productResults?.totalRatingBreakup).map((item) => {
                const starColor = getStarColor(Number(item.label.trim()));
                return {
                    label: (
                        <>
                            {item.label.trim()} <span style={{ color: starColor }}>★</span>
                        </>
                    ),
                    value: item.value,
                    color: starColor,
                };
            }),
        [productResults?.totalRatingBreakup]
    );
    const npsBuckets = useMemo(
        () => getNpsBuckets(productResults?.totalRatingBreakup),
        [productResults?.totalRatingBreakup]
    );

    const attributeRatings = productResults?.attributeRatings || [];

    // Server-generated stamped product images (the marketing "razítko" the client
    // downloads). May be several — that's the "batch".
    const stampedImages = useMemo(() => {
        const products = campaign?.products || [];
        const product = products.find((item) => item?.id === productId) || products[0];
        return (product?.stampedImages || [])
            .map((stamp) => stamp?.image)
            .filter((image): image is File => !!image?.src);
    }, [campaign?.products, productId]);
    const recommend = Math.round(productResults?.wouldRecommendPercent ?? 0);
    // Year baked onto the seal: the campaign-finished milestone (the closest client-side proxy for
    // the last approved review's year), falling back to the registration-close date, then this year.
    const stampYear = useMemo(() => {
        const finishedAt = (campaign?.milestones || []).find(
            (milestone) => milestone?.type === `state:${CampaignState.Finished}`
        )?.timestamp;
        const timestamp = finishedAt ?? campaign?.settings?.closeAt;
        return timestamp ? dayjs(timestamp).year() : dayjs().year();
    }, [campaign?.milestones, campaign?.settings?.closeAt]);
    // The static seal only exists for 80–100 %; below that the campaign gets no razítko.
    const badgeSrc = stampBadgeSrc(recommend, stampYear);
    // Show the seal card only when there's something to show: a server-generated stamped image,
    // or a recommend-% at/above the 80 % award threshold.
    const hasStamp = stampedImages.length > 0 || badgeSrc != null;

    const [isDownloading, setIsDownloading] = useState(false);

    // Download the stamped images at full resolution; if none exist yet, download the Testuj.to
    // seal PNG for this campaign's recommend-% and year. The button only renders when a seal
    // exists (see hasStamp), so there is always something to download here.
    const handleDownloadStamp = async () => {
        setIsDownloading(true);
        try {
            if (stampedImages.length > 0) {
                for (const image of stampedImages) {
                    await downloadImage(image);
                }
            } else {
                downloadStampBadge(recommend, stampYear);
            }
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <ReportSection title={t("campaign_report_evaluation")}>
            <Grid>
                {/* Row 1: popularity + NPS */}
                <Grid.Col md={6}>
                    <Paper withBorder radius={16} p={24} style={cardStyle}>
                        <Text mb={20} weight={700} color="#0F1B3D">
                            {t("campaign_report_popularity")}
                        </Text>
                        <BarList items={ratingItems} labelWidth={56} />
                    </Paper>
                </Grid.Col>
                <Grid.Col md={6}>
                    <Paper withBorder radius={16} p={24} style={cardStyle}>
                        <Text mb={20} weight={700} color="#0F1B3D">
                            {t("campaign_report_nps")}
                        </Text>
                        {/* NpsScoreCard renders its own nested white card + heading. Strip that chrome
                            and hide its title so it blends into this Paper and matches the sibling card. */}
                        <Box
                            sx={{
                                "& .tt-gauge-chart": {
                                    padding: 0,
                                    borderRadius: 0,
                                    background: "transparent",
                                    minWidth: 0,
                                },
                                "& .tt-gauge-chart > p": { display: "none" },
                            }}
                        >
                            <NpsScoreCard
                                promoters={npsBuckets?.promoters || 0}
                                passives={npsBuckets?.passives || 0}
                                detractors={npsBuckets?.detractors || 0}
                                t={t}
                            />
                        </Box>
                    </Paper>
                </Grid.Col>

                {/* Row 2: product attributes + stamp. The attributes card spans full width when the
                    campaign earned no seal, so the row doesn't leave a lonely half-empty cell. */}
                <Grid.Col md={hasStamp ? 6 : 12}>
                    <Paper withBorder radius={16} p={24} style={cardStyle}>
                        <Text weight={700} color="#0F1B3D">
                            {t("campaign_overview_product_properties")}
                        </Text>
                        {attributeRatings.length === 0 ? (
                            <Text mt={16} size="sm" color="dimmed">
                                {t("campaign_report_no_data")}
                            </Text>
                        ) : (
                            <>
                                <Text mb={20} size="sm" color="dimmed">
                                    {t("campaign_report_attributes_hint")}
                                </Text>
                                {/* Per-attribute mean tester rating, shown as "X / 5" to match the overall
                                    score idiom. Stored 1–10 (= stars × 2): /2 for the label, /10 for the fill. */}
                                <Stack spacing={16}>
                                    {attributeRatings.map((attribute) => {
                                        const rating = attribute.rating ?? 0;
                                        const fill = Math.min(Math.max(rating / 10, 0), 1) * 100;
                                        return (
                                            <Group
                                                key={attribute.id ?? attribute.name}
                                                spacing={12}
                                                noWrap
                                                align="center"
                                            >
                                                <Text
                                                    size="sm"
                                                    weight={600}
                                                    color="#0F1B3D"
                                                    style={{ width: 120, flex: "none" }}
                                                >
                                                    {attribute.name}
                                                </Text>
                                                <Box
                                                    style={{
                                                        flex: 1,
                                                        height: 12,
                                                        background: "#EEF1F6",
                                                        borderRadius: 999,
                                                    }}
                                                >
                                                    <Box
                                                        style={{
                                                            width: `${fill}%`,
                                                            height: 12,
                                                            background: getStarColor(rating / 2),
                                                            borderRadius: 999,
                                                        }}
                                                    />
                                                </Box>
                                                <Text
                                                    weight={700}
                                                    color="#0F1B3D"
                                                    style={{
                                                        minWidth: 52,
                                                        textAlign: "right",
                                                        fontVariantNumeric: "tabular-nums",
                                                    }}
                                                >
                                                    {(rating / 2).toFixed(1)} / 5
                                                </Text>
                                            </Group>
                                        );
                                    })}
                                </Stack>
                            </>
                        )}
                    </Paper>
                </Grid.Col>
                {hasStamp && (
                    <Grid.Col md={6}>
                        <Paper withBorder radius={16} p={24} style={cardStyle}>
                            <Stack align="center" spacing="md" style={{ height: "100%", justifyContent: "center" }}>
                                {stampedImages[0]?.src ? (
                                    <Image
                                        src={`${stampedImages[0].src}?w=240`}
                                        width={120}
                                        height={120}
                                        fit="contain"
                                    />
                                ) : badgeSrc ? (
                                    <Image src={badgeSrc} width={140} height={140} fit="contain" />
                                ) : null}
                                <div style={{ textAlign: "center" }}>
                                    <Text weight={700} color="#0F1B3D">
                                        {t("campaign_report_stamp")}
                                    </Text>
                                    <Text size="sm" color="dimmed">
                                        {t("campaign_report_stamp_hint")}
                                    </Text>
                                </div>
                                <Button
                                    variant="light"
                                    color="blue"
                                    radius="md"
                                    leftIcon={<IconDownload size={16} />}
                                    loading={isDownloading}
                                    onClick={handleDownloadStamp}
                                >
                                    {stampedImages.length > 1
                                        ? `${t("campaign_report_download_png")} (${stampedImages.length})`
                                        : t("campaign_report_download_png")}
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid.Col>
                )}
            </Grid>
        </ReportSection>
    );
};
